/**
 * Disable 2FA Modal
 *
 * Modal para desactivar autenticación de dos factores
 * Requiere código válido para confirmar
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ShieldOff,
  Loader2,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { Input } from '@/presentation/components/ui/Input'
import { Button } from '@/presentation/components/ui/Button'
import { useDisable2FA } from '@/application/auth/useTwoFactor'
import { toast } from '@/lib/toast'

// =============================================================================
// Schema
// =============================================================================

const disableSchema = z.object({
  code: z.string().min(6, 'El código debe tener al menos 6 caracteres').max(10),
})

type DisableForm = z.infer<typeof disableSchema>

// =============================================================================
// Types
// =============================================================================

interface Disable2FAModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

// =============================================================================
// Component
// =============================================================================

export function Disable2FAModal({ open, onOpenChange, onSuccess }: Disable2FAModalProps) {
  const [error, setError] = useState<string | null>(null)
  const disableMutation = useDisable2FA()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DisableForm>({
    resolver: zodResolver(disableSchema),
  })

  const onSubmit = async (data: DisableForm) => {
    setError(null)

    try {
      await disableMutation.mutateAsync(data.code)
      toast.success('2FA desactivado correctamente')
      onSuccess?.()
      onOpenChange(false)
      reset()
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error desactivando 2FA'
      setError(message)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset()
      setError(null)
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="h-5 w-5 text-[var(--destructive)]" />
            Desactivar 2FA
          </DialogTitle>
          <DialogDescription>
            Ingresa un código para confirmar la desactivación
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Warning */}
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-600 dark:text-amber-400">
                <strong>Advertencia:</strong> Al desactivar 2FA, tu cuenta será menos segura.
                Solo podrás iniciar sesión con tu contraseña.
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-[var(--destructive)]/10 border border-[var(--destructive)]/20 text-[var(--destructive)]">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <Input
            label="Código de verificación"
            placeholder="Código de tu app o código de respaldo"
            error={errors.code?.message}
            {...register('code')}
          />

          <p className="text-xs text-[var(--foreground-muted)]">
            Ingresa un código de 6 dígitos de tu aplicación de autenticación
            o uno de tus códigos de respaldo.
          </p>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={disableMutation.isPending}
            >
              {disableMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Desactivando...
                </>
              ) : (
                'Desactivar 2FA'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
